import React, { PureComponent } from 'react'
import {
  string, func, node, shape,
} from 'prop-types'
import ReactDOMServer from 'react-dom/server'

import Button from '@active/react-ui/Button'
import Checkbox from '@active/react-ui/Checkbox'
import { withI18n } from '@active/react-utils/i18n'
import { getRootElement } from '@active/react-ui/config'

import DefaultComponentLayout, { LayoutContent } from '../common/Layout'
import I18nMessage from '../common/I18nMessage'
import CommonError from '../common/CommonError'
import {
  noop,
} from '../../shared/utils/commonUtils'
import ConfirmPrompt from './ConfirmPrompt'
import connectLocale from '../../shared/utils/connectLocale'

import './privacyManagement.scss'

class PrivacyManagement extends PureComponent {
  static propTypes = {
    locale: string.isRequired,
    i18n: func.isRequired,
    content: node,
    passportAPIServerHost: string.isRequired,
    services: shape({
      getSignedWaivers: func.isRequired,
      revokeSignedAgreements: func.isRequired,
    }).isRequired,
    actions: shape({
      logoutCallback: func.isRequired,
    }).isRequired,
    login: func.isRequired,
  }

  static defaultProps = {
    content: null,
  }

  constructor(props) {
    super(props)
    this.state = {}
    this.agreementGroupList = {}
  }

  componentDidMount() {
    const {
      locale, services: { getSignedWaivers }, login,
    } = this.props
    getSignedWaivers({
      params: { waiverLocale: locale },
      resolve: ({ status, waivers }) => {
        if (status === 'SUCCESS') {
          this.setState({ waivers })
        } else {
          login()
        }
      },
      reject: () => {
        login()
      },
    })
  }

  buildContent = () => {
    const { content } = this.props
    return content || (
      <div className="passport-privacy-content">
        <p className="text-font-14">
          <I18nMessage id="passport.privacy.notice" />
        </p>
        <p className="text-font-14 passport__pt10 passport__pb10">
          <I18nMessage id="passport.privacy.notice.argeement" />
        </p>
      </div>
    )
  }

  buildWaivers = () => {
    const { waivers } = this.state
    this.agreementGroupList = {}

    const res = new Map()
    return waivers
      // Filter all duplicated waivers (caused by reused waivers by different attachment types) by waiver id
      .filter(w => !res.has(w.waiverId) && res.set(w.waiverId, 1))
      // ACTIVE > GDPR > PASSPORT_C > PASSPORT_E. Can enhance sequences in enum data in future
      .sort((a, b) => {
        if (a.waiverAttachmentType < b.waiverAttachmentType) {
          return -1
        }
        if (a.waiverAttachmentType > b.waiverAttachmentType) {
          return 1
        }
        return 0
      })
      .map(({
        waiverAgreementId, waiverId, waiverLabel, waiverName, ...rest
      }) => {
        const { i18n } = this.props

        const label = waiverLabel && waiverLabel.length
          ? waiverLabel
          : i18n('passport.waiver.terms.active_existAccount')

        const checkboxAriaLabel = label.replace('{0}', waiverName)

        return (
          <div key={waiverAgreementId} className="passport-checkbox">
            <Checkbox
              value={waiverAgreementId}
              defaultChecked
              onChange={this.handleAgreementsCheckState}
              // Push ref into agreementGroupList for handleLabelClick to transfer click event
              ref={e => { if (e) this.agreementGroupList[waiverId] = e }}
              aria-label={checkboxAriaLabel}
            />
            {this.buildWaiverContent({
              waiverId, waiverLabel, waiverName, ...rest,
            })}
          </div>
        )
      })
  }

  buildWaiverContent = ({
    waiverId, waiverIdentity, waiverName, waiverLabel, locale,
  }) => {
    const { passportAPIServerHost, i18n } = this.props

    const label = waiverLabel && waiverLabel.length
      ? waiverLabel
      : i18n('passport.waiver.terms.active_existAccount')

    const title = label.replace('{0}', waiverName)

    const waiverLink = ReactDOMServer.renderToString((
      <a
        className="passport__pl5"
        target="_blank"
        rel="nofollow noopener noreferrer"
        href={`${passportAPIServerHost}/waiver?waiverId=${waiverIdentity}&waiverLocale=${locale}`}
        // Make the whole sentence as title
        title={waiverName}
      >
        {waiverName}
      </a>
    ))

    return (
      <span
        className="passport-checkbox-label text-font-12"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: label.replace('{0}', waiverLink) }}
        // Make sure clicking label is the same as clicking check box directly
        onClick={this.handleLabelClick}
        // Add a customized attribute value (by waiver Id) only for handleLabelClick to locate Ref from list
        value={waiverId}
        title={title}
      />
    )
  }

  handleClickBtn = () => {
    const { revokeAgreementsIds = [] } = this.state
    if (revokeAgreementsIds.length) {
      this.setState({ showPrompt: true })
    }
  }

  handleAgreementsCheckState = ({ target: { value, checked } }) => {
    const { revokeAgreementsIds = [] } = this.state
    const isAdded = revokeAgreementsIds.includes(value)
    checked && isAdded && revokeAgreementsIds.splice(revokeAgreementsIds.indexOf(value), 1)
    !checked && !isAdded && revokeAgreementsIds.push(value)
    this.setState({ revokeAgreementsIds })
    this.forceUpdate()
  }

  /**
   * Make sure clicking label is the same as clicking check box directly
   * Transfer click event from waiver label to waiver checkbox if user click waiver label
   */
  handleLabelClick = ({ target }) => {
    const targetCheckBox = this.agreementGroupList[target.getAttribute('value')]
    if (targetCheckBox) {
      // targetCheckBox.checkboxComponent.input.click()
      targetCheckBox.click()
    }
  }

  handleConfirmPromptClose = e => {
    this.setState({ showPrompt: false })

    if (e.keyCode === 13 || e.keyCode === 32) {
      setTimeout(() => {
        const focusElement = getRootElement().querySelector('.passport-ui-app .passport-privacy .passport-privacy__btn')

        if (focusElement) {
          focusElement.focus && focusElement.focus()
        }
      }, 500)
    }
  }

  confirmActionCallback = () => {
    const {
      services: { revokeSignedAgreements },
      actions: { logoutCallback },
    } = this.props
    const { revokeAgreementsIds } = this.state
    this.setState({ showPrompt: false }, () => {
      revokeSignedAgreements({
        params: { agreementIds: revokeAgreementsIds.join(',') },
        reject: noop,
        complete: () => {
          this.setState({
            waivers: null,
            revokeAgreementsIds: [],
          }, logoutCallback)
        },
      })
    })
  }

  render() {
    const {
      waivers,
      error,
      showPrompt,
      revokeAgreementsIds,
    } = this.state
    if (error) {
      return (
        <CommonError {...error} />
      )
    }

    if (!waivers) {
      return null
    }
    return (
      <DefaultComponentLayout nameSpace="passport-privacy">
        <LayoutContent title message="passport.privacy.title" />
        <LayoutContent subTitle message="passport.privacy.subTitle" />
        <LayoutContent main>
          {showPrompt
            && (
              <ConfirmPrompt
                confirmActionCallback={this.confirmActionCallback}
                onClose={this.handleConfirmPromptClose}
              />
            )}
          {this.buildContent()}
          {this.buildWaivers()}
          <Button
            type="primary"
            disabled={!revokeAgreementsIds || revokeAgreementsIds.length === 0}
            className="passport-privacy__btn"
            onClick={this.handleClickBtn}
          >
            <I18nMessage id="passport.common.btn.update" />
          </Button>
        </LayoutContent>
      </DefaultComponentLayout>
    )
  }
}

export default withI18n(connectLocale(PrivacyManagement))
